// Sun Valley : portail de retour maison (sections/sv-retours.liquid). Dialogue avec le service de
// retours de l'app Pilote : POST /api/retours/commande puis POST /api/retours/demande.
// Aucune donnée n'est gardée dans le navigateur au-delà de la page.
(() => {
  if (customElements.get('sv-retours')) return;

  // Textes traduits (snippets/sv-textes-js.liquid) ; à défaut, la clé
  const T = (cle, v) => (window.svT ? window.svT(cle, v) : cle);

  const ech = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const euros = (n) => Number(n || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  const date = (s) => (s ? new Date(s).toLocaleDateString(document.documentElement.lang || 'fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

  customElements.define('sv-retours', class extends HTMLElement {
    connectedCallback() {
      this.api = (this.dataset.api || '').replace(/\/$/, '');
      this.message = this.querySelector('.sv-retours__message');
      this.etat = null;

      this.querySelector('[data-etape="1"]').addEventListener('submit', (e) => { e.preventDefault(); this.rechercher(); });
      this.querySelectorAll('[data-retour]').forEach((b) => b.addEventListener('click', () => this.aller(Number(b.dataset.retour))));
      this.querySelector('[data-suivant="3"]').addEventListener('click', () => this.recapituler());
      this.querySelector('[data-envoyer]').addEventListener('click', () => this.envoyer());

      // Lien direct depuis un e-mail ou le compte client : ?commande=SV26093 (et e-mail prérempli si connecté)
      const f = this.querySelector('[data-etape="1"]');
      const numero = new URLSearchParams(location.search).get('commande');
      if (numero) f.numero.value = numero.slice(0, 20);
      if (f.numero.value && f.email.value) this.rechercher();
    }

    dire(texte) {
      this.message.textContent = texte || '';
      this.message.hidden = !texte;
      if (texte) this.message.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    aller(n) {
      this.dire('');
      this.querySelectorAll('[data-etape]').forEach((el) => { el.hidden = Number(el.dataset.etape) !== n; });
      let libelle = '';
      this.querySelectorAll('[data-etape-marque]').forEach((el) => {
        const m = Number(el.dataset.etapeMarque);
        el.toggleAttribute('aria-current', m === n);
        el.classList.toggle('is-fait', m < n);
        if (m === n) libelle = el.textContent.replace(/^\s*\d+\s*/, '').trim().toLowerCase();
      });
      // Sur mobile, les libellés des étapes sont masqués faute de place : cette ligne
      // dit où en est le client.
      const courante = this.querySelector('[data-etape-courante]');
      if (courante) courante.textContent = T('retours.etape_courante', { n, libelle });
      this.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    async appeler(chemin, corps, bouton) {
      if (!this.api) { this.dire(T('retours.non_configure')); return null; }
      const libelle = bouton ? bouton.textContent : '';
      if (bouton) { bouton.disabled = true; bouton.textContent = T('retours.un_instant'); }
      try {
        // Le service répond dans la langue de la page (site anglais : messages traduits côté Pilote).
        const langue = (document.documentElement.lang || 'fr').slice(0, 2);
        const r = await fetch(`${this.api}${chemin}?langue=${encodeURIComponent(langue)}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corps) });
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { this.dire(j.erreur || T('retours.erreur')); return null; }
        return j;
      } catch (e) {
        this.dire(T('retours.connexion'));
        return null;
      } finally {
        if (bouton) { bouton.disabled = false; bouton.textContent = libelle; }
      }
    }

    async rechercher() {
      const f = this.querySelector('[data-etape="1"]');
      const numero = f.numero.value.trim();
      const email = f.email.value.trim();
      if (!numero || !email) { this.dire(T('retours.numero_email_manquants')); return; }
      const j = await this.appeler('/api/retours/commande', { numero, email }, f.querySelector('button[type="submit"]'));
      if (!j) return;

      // Rien à retourner : on le dit tout de suite, avec la vraie raison. Faire avancer
      // le client d'une étape pour lui annoncer qu'il n'a rien à y faire est une
      // impasse, et il repart sans savoir quoi faire.
      const blocage = this.raisonDeBlocage(j);
      if (blocage) { this.dire(blocage); return; }

      this.etat = j;
      this.rendreArticles();
      this.aller(2);
    }

    /** Message à afficher quand aucun article n'est retournable, sinon rien. */
    raisonDeBlocage({ commande, articles, politique }) {
      const retournables = articles.filter((a) => a.eligible);
      if (retournables.length) return '';

      const enCours = (commande.retoursExistants || []).filter((r) => r.statut !== 'CANCELED');
      if (enCours.length) {
        return T('retours.deja_en_cours', { commande: commande.nom, retours: enCours.map((r) => r.nom).join(', ') });
      }
      if (!articles.length) {
        return T('retours.pas_expediee', { commande: commande.nom, jours: politique.delaiJours });
      }
      // Des articles existent mais aucun n'est éligible : c'est le délai qui est passé.
      const raison = articles.find((a) => a.raison)?.raison;
      return raison
        ? T('retours.raison_contact', { raison })
        : T('retours.plus_retournable', { commande: commande.nom });
    }

    rendreArticles() {
      const { commande, articles, motifs } = this.etat;
      this.querySelector('[data-commande-nom]').textContent = T('retours.commande_du', { commande: commande.nom, date: date(commande.passeeLe) });
      const liste = this.querySelector('[data-articles]');
      if (!articles.length) {
        liste.innerHTML = `<li class="sv-retours__vide">${T('retours.aucun_article')}${commande.retoursExistants.length ? ` ${T('retours.retours_en_cours', { retours: commande.retoursExistants.map((r) => ech(r.nom)).join(', ') })}` : ''}</li>`;
        this.querySelector('[data-suivant="3"]').hidden = true;
        return;
      }
      this.querySelector('[data-suivant="3"]').hidden = false;
      const optionsMotifs = motifs.map((m) => `<option value="${ech(m.code)}">${ech(m.libelle)}</option>`).join('');
      liste.innerHTML = articles.map((a, i) => {
        const quantites = Array.from({ length: a.quantite }, (_, k) => `<option value="${k + 1}">${k + 1}</option>`).join('');
        const tailles = a.echanges.map((e) => `<option value="${ech(e.variantId)}">${ech(e.taille)}</option>`).join('');
        // La carte entière est cliquable : le repère de sélection est dessiné, la case
        // du navigateur reste là pour le clavier et les lecteurs d'écran.
        return `<li class="sv-retours__article${a.eligible ? '' : ' is-inactif'}" data-index="${i}">
          <label class="sv-retours__choix">
            <input type="checkbox" class="sv-retours__coche" data-coche ${a.eligible ? '' : 'disabled'}>
            ${a.image ? `<img src="${ech(a.image)}" alt="" width="72" height="90" loading="lazy">` : '<span class="sv-retours__sans-image"></span>'}
            <span class="sv-retours__desc">
              <span class="sv-retours__nom">${ech(a.titre)}</span>
              <span class="sv-retours__variante">${ech(a.variante)}${a.quantite > 1 ? `, ${T('retours.pieces', { n: a.quantite })}` : ''}</span>
              ${a.prix ? `<span class="sv-retours__prix">${euros(a.prix)}</span>` : ''}
              ${a.eligible ? '' : `<span class="sv-retours__raison">${ech(a.raison || T('retours.non_retournable'))}</span>`}
            </span>
            <span class="sv-retours__marque" aria-hidden="true"></span>
          </label>
          <div class="sv-retours__details" hidden>
            <fieldset class="sv-retours__souhait">
              <legend>${T('retours.je_souhaite')}</legend>
              <div class="sv-retours__segments">
                <label class="sv-retours__segment">
                  <input type="radio" name="souhait-${i}" value="echange" ${a.echanges.length ? '' : 'disabled'}>
                  <span>${a.echanges.length ? T('retours.echanger_taille') : T('retours.echange_indisponible')}</span>
                </label>
                <label class="sv-retours__segment">
                  <input type="radio" name="souhait-${i}" value="remboursement">
                  <span>${T('retours.etre_rembourse')}</span>
                </label>
              </div>
            </fieldset>
            ${a.echanges.length ? `<label class="sv-retours__champ sv-retours__taille" hidden><span>${T('retours.nouvelle_taille')}</span><select data-taille><option value="">${T('retours.choisissez_taille')}</option>${tailles}</select></label>` : ''}
            ${a.quantite > 1 ? `<label class="sv-retours__champ"><span>${T('retours.quantite_retourner')}</span><select data-quantite>${quantites}</select></label>` : ''}
            <label class="sv-retours__champ"><span>${T('retours.motif')}</span><select data-motif required><option value="">${T('retours.choisissez_motif')}</option>${optionsMotifs}</select></label>
            <label class="sv-retours__champ"><span>${T('retours.commentaire')}</span><textarea data-commentaire rows="2" maxlength="300" placeholder="${ech(T('retours.commentaire_placeholder'))}"></textarea></label>
          </div>
        </li>`;
      }).join('');

      liste.querySelectorAll('.sv-retours__article').forEach((li) => {
        const coche = li.querySelector('[data-coche]');
        const details = li.querySelector('.sv-retours__details');
        coche.addEventListener('change', () => { details.hidden = !coche.checked; li.classList.toggle('is-coche', coche.checked); });
        li.querySelectorAll('input[type="radio"]').forEach((r) => r.addEventListener('change', () => {
          const t = li.querySelector('.sv-retours__taille');
          const echange = li.querySelector('input[value="echange"]')?.checked;
          if (t) t.hidden = !echange;
        }));
      });
    }

    lireLignes() {
      const lignes = [];
      for (const li of this.querySelectorAll('.sv-retours__article')) {
        if (!li.querySelector('[data-coche]')?.checked) continue;
        const a = this.etat.articles[Number(li.dataset.index)];
        const motif = li.querySelector('[data-motif]').value;
        const souhait = li.querySelector('input[type="radio"]:checked')?.value;
        const taille = li.querySelector('[data-taille]')?.value || null;
        if (!motif) return { erreur: T('retours.erreur_motif', { article: a.titre }) };
        if (!souhait) return { erreur: T('retours.erreur_souhait', { article: a.titre }) };
        if (souhait === 'echange' && !taille) return { erreur: T('retours.erreur_taille', { article: a.titre }) };
        lignes.push({
          id: a.id,
          quantite: Number(li.querySelector('[data-quantite]')?.value || 1),
          motif,
          souhait,
          echangeVariantId: souhait === 'echange' ? taille : null,
          commentaire: li.querySelector('[data-commentaire]').value.trim(),
          _article: a,
          _motif: this.etat.motifs.find((m) => m.code === motif)?.libelle,
          _taille: souhait === 'echange' ? a.echanges.find((e) => e.variantId === taille)?.taille : null,
        });
      }
      if (!lignes.length) return { erreur: T('retours.erreur_aucun') };
      return { lignes };
    }

    recapituler() {
      const { erreur, lignes } = this.lireLignes();
      if (erreur) { this.dire(erreur); return; }
      this.lignes = lignes;
      const { commande, politique } = this.etat;
      const qRemb = lignes.filter((l) => l.souhait === 'remboursement').reduce((s, l) => s + l.quantite, 0);
      const qEch = lignes.filter((l) => l.souhait === 'echange').reduce((s, l) => s + l.quantite, 0);
      const montant = lignes.filter((l) => l.souhait === 'remboursement').reduce((s, l) => s + l._article.prix * l.quantite, 0);
      // Frais de retour : rien sur un échange, le port de la zone dès qu’un article part en remboursement.
      const retenue = qRemb > 0 ? (commande.fraisRetour || 0) : 0;
      // Toute la commande remboursée : rétractation totale, la livraison payée est remboursée
      // aussi (article L221-24). Même règle que lib/retours/demande.ts, qui fait foi.
      const totale = qRemb > 0 && !qEch && qRemb >= (commande.quantiteCommandee || Infinity)
        && !(commande.retoursExistants || []).some((r) => r.statut !== 'CANCELED');
      const livraison = totale ? (commande.fraisPort || 0) : 0;

      // Un seul message sur l'argent, et il s'adapte : gratuit quand tout part en
      // échange, chiffré quand un article est remboursé. Avant, trois phrases se
      // chevauchaient et l'une d'elles parlait de frais alors qu'il n'y en avait pas.
      const compte = [
        qEch ? T(qEch > 1 ? 'retours.echange_plusieurs' : 'retours.echange_un', { n: qEch }) : '',
        qRemb ? T(qRemb > 1 ? 'retours.remboursement_plusieurs' : 'retours.remboursement_un', { n: qRemb }) : '',
      ].filter(Boolean).join(` ${T('retours.et')} `);

      const argent = qRemb
        ? `<div class="sv-retours__total">
             <div class="sv-retours__total-ligne"><span>${T('retours.articles_rembourses')}</span><span>${euros(montant)}</span></div>
             ${livraison ? `<div class="sv-retours__total-ligne"><span>${T('retours.livraison_remboursee')}</span><span>${euros(livraison)}</span></div>` : ''}
             ${retenue ? `<div class="sv-retours__total-ligne sv-retours__total-ligne--retenue"><span>${T('retours.frais_retour')}</span><span>-${euros(retenue)}</span></div>` : ''}
             <div class="sv-retours__total-ligne sv-retours__total-ligne--fort"><span>${T('retours.remboursement_estime')}</span><span>${euros(Math.max(0, montant + livraison - retenue))}</span></div>
           </div>
           ${qEch ? `<p class="sv-retours__note">${T('retours.echanges_gratuits')}</p>` : ''}`
        : `<p class="sv-retours__gratuit">${T('retours.retour_gratuit')}</p>`;

      this.querySelector('[data-recap]').innerHTML = `
        <p class="sv-retours__compte">${compte}</p>
        <ul class="sv-retours__lignes unstyled-list">
          ${lignes.map((l) => `<li class="sv-retours__ligne">
            ${l._article.image ? `<img src="${ech(l._article.image)}" alt="" width="56" height="70" loading="lazy">` : '<span class="sv-retours__sans-image"></span>'}
            <span class="sv-retours__ligne-texte">
              <span class="sv-retours__nom">${ech(l._article.titre)}</span>
              <span class="sv-retours__variante">${ech(l._article.variante)}${l.quantite > 1 ? `, ${T('retours.pieces', { n: l.quantite })}` : ''}</span>
              <span class="sv-retours__action">${l.souhait === 'echange' ? T('retours.echange_contre', { taille: ech(l._taille) }) : T('retours.remboursement_montant', { montant: euros(l._article.prix * l.quantite) })}</span>
              <span class="sv-retours__motif">${ech(l._motif)}</span>
            </span>
          </li>`).join('')}
        </ul>
        ${argent}
        <div class="sv-retours__envoi">
          ${commande.etiquetteFournie
            ? `<p>${T('retours.etiquette_fournie_html')}</p>`
            : `<p>${T('retours.a_votre_charge_html')}</p><p class="sv-retours__adresse">${politique.adresseRetour.map(ech).join('<br>')}</p>`}
          <p class="sv-retours__note">${T('retours.delai_traitement', { delai: ech(politique.delaiTraitement) })}</p>
        </div>`;
      this.aller(3);
    }

    async envoyer() {
      const lignes = this.lignes.map(({ _article, _motif, _taille, ...l }) => l);
      const j = await this.appeler('/api/retours/demande', { jeton: this.etat.jeton, lignes }, this.querySelector('[data-envoyer]'));
      if (!j) return;
      this.querySelector('[data-confirmation-titre]').textContent = T('retours.retour_enregistre', { retour: j.retour.nom });
      this.querySelector('[data-confirmation]').innerHTML = `
        <p>${T('retours.email_confirmation')}</p>
        ${j.etiquette
          ? `<p class="sv-retours__telecharger"><a class="button button--xl" data-etiquette download="etiquette-retour-${ech(j.retour.nom)}.pdf">${T('retours.telecharger')}</a></p>
             <p class="sv-retours__suivi"><span>${T('retours.numero_suivi')}</span><a href="${ech(j.etiquette.suivi)}" target="_blank" rel="noopener">${ech(j.etiquette.numero)}</a></p>
             <ul class="sv-retours__jalons">
               <li class="is-fait">${T('retours.jalon_1')}</li>
               <li>${T('retours.jalon_2')}</li>
               <li>${T('retours.jalon_3')}</li>
               <li>${j.articles.some((a) => a.souhait === 'echange') ? T('retours.jalon_echange') : T('retours.jalon_remboursement')}</li>
             </ul>
             <p class="sv-retours__note">${T('retours.etiquette_jointe')}</p>`
          : j.etiquetteFournie
          ? `<p>${T('retours.etiquette_24h')}</p>`
          : `<p>${T('retours.renvoyer_adresse_html', { retour: ech(j.retour.nom) })}</p><p class="sv-retours__adresse">${j.adresseRetour.map(ech).join('<br>')}</p>`}
        ${j.remboursementEstime > 0 ? `<p class="sv-retours__montant">${T('retours.remboursement_estime_html', { montant: euros(j.remboursementEstime) })}</p>` : ''}
        <p>${T('retours.traitement_court', { delai: ech(j.delaiTraitement) })}</p>`;
      const lien = this.querySelector('[data-etiquette]');
      if (lien && j.etiquettePdf) {
        const octets = Uint8Array.from(atob(j.etiquettePdf), (c) => c.charCodeAt(0));
        lien.href = URL.createObjectURL(new Blob([octets], { type: 'application/pdf' }));
      }
      this.aller(4);
    }
  });
})();
